import { IPageConfiguration } from '../../../../domain/conf/page'
import { logger } from '../../../../utils/common/logger'
import { DOMEvents, DOMUtils } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { LocalStore } from '../../../../utils/storage/local'
import { AnalyticsService } from '../../../analytics'
import { IdentityEvent } from '../../../common/events/identity'
import { PageType } from '../../../common/page-types'
import { IProfile } from '../../../common/profile'
import { ISophiPaywallOffer, ISubscriberServiceAdapter } from '../adapter'

export interface IWindow extends Window {
    sophi: any
    demeter: any
}

export enum LoginStatus {
    ANONYMOUS = 'anonymous',
    REGISTERED = 'registered',
    SUBSCRIBED = 'subscribed'
}

export enum WallVisibility {
    ALWAYS = 'always',
    NEVER = 'never'
}

export enum WallType {
    REGWALL = 'regwall',
    PAYWALL = 'paywall'
}

export enum AbTestAssignedGroup {
    CONTROL = 'control',
    VARIANT = 'variant'
}

declare let window: IWindow

export class Sophi {
    constructor(private subscriberService: ISubscriberServiceAdapter,
                private pageConf: IPageConfiguration, private paywallOfferConf: ISophiPaywallOffer,
                private useSophiDecision = true,
                private isGNSEnabled = false) {}

    /**
     * Initialize Sophi
     */
    public async init() {
        const sophi = window.sophi
        const demeter = window.demeter
        const type = this.pageConf.type
        const ancestry = this.pageConf.ancestry
        const isPaywallWhitelisted = this.pageConf.isPaywallWhitelisted
        const isPremium = this.pageConf.isPremium
        const isRegistration = this.pageConf.isRegistration
        let enabled = true

        if (sophi && demeter) {
            // Get test cookie
            const abTestName = (document.cookie.split(';').find((c) =>
                c.trim().startsWith(`${Store.SOPHI_AB_TEST_NAME}=`)) || '').trim().split('=').pop() || undefined
            const abTestAssignedGroup = (document.cookie.split(';').find((c) =>
                c.trim().startsWith(`${Store.SOPHI_AB_TEST}=`)) || '').trim().split('=').pop() || undefined
            const variantName = (abTestName && abTestAssignedGroup) ? `${abTestName}:${abTestAssignedGroup}` : undefined

            if (abTestAssignedGroup) {
                logger('info', `Sophi.init - assigned A/B test name: ${abTestName}`)
                logger('info', `Sophi.init - assigned A/B test group: ${abTestAssignedGroup}`)

                if (abTestAssignedGroup !== AbTestAssignedGroup.VARIANT) {
                    // Turn Sophi off if part of control group
                    enabled = false
                }
            }

            // Dispatch pageview event
            demeter('pageview', {
                args: {
                    article: (type === PageType.STORY) ? true : false,
                    section: (Array.isArray(ancestry)) ? ancestry[0] : 'index'
                }
            })
            logger('info', 'Sophi.init - Sophi page view event dispatched')

            if (enabled) {
                const loginStatus = this.getLoginStatus()

                // Exclude whitelisted, premium and GNS articles from Sophi paywall
                if (type === PageType.STORY && !isPaywallWhitelisted &&
                    !isPremium && !this.isGNSEnabled && !isRegistration
                ) {
                    if (loginStatus === LoginStatus.REGISTERED || loginStatus === LoginStatus.ANONYMOUS) {
                        try {
                            if (this.useSophiDecision) {
                                const decision = await demeter('getDecision', {
                                    args: {
                                        visitor: loginStatus
                                    }
                                })
                                logger('info', `Sophi.init - Sophi decision: ${JSON.stringify(decision)}`)

                                // Send meter execute event
                                addEventListener(IdentityEvent.MP_READY, () => {
                                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                                        detail: {
                                            context: decision.context,
                                            control_fields: {
                                                mparticle: {
                                                    keys: {
                                                        context: 'context',
                                                        inputs: 'inputs',
                                                        meter_name: 'meter_name',
                                                        trace: 'trace',
                                                        variant_name: 'variant_name'
                                                    },
                                                    mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                                    mp_event_type: 'UserPreference'
                                                }
                                            },
                                            event_name: AnalyticsService.customEventNames.meterExecute,
                                            inputs: decision.inputs,
                                            meter_name: 'sophi_meter',
                                            trace: decision.trace,
                                            variant_name: variantName
                                        }
                                    }))
                                })

                                if (decision.outcome && decision.outcome.wallVisibility === WallVisibility.ALWAYS) {
                                    const wallType = decision.outcome.wallType

                                    if (wallType === WallType.PAYWALL) {
                                        // Dispatch paywall event
                                        demeter('wall', {
                                            args: 'paywall'
                                        })
                                        logger('info', 'Sophi.init - Sophi paywall event dispatched')

                                        // Show paywall
                                        logger('info', 'Sophi.init - Show paywall')
                                        this.showPaywall()

                                        if (this.paywallOfferConf) {
                                            // Show Piano offer
                                            this.subscriberService.showOffer(
                                                this.paywallOfferConf.offerId, // Offer id
                                                this.paywallOfferConf.templateId, // Template id
                                                'inline', // Dispaly mode
                                                '#identity-screen-offer' // Container selector
                                            )
                                        }
                                    } else {
                                        // Dispatch regwall event
                                        demeter('wall', {
                                            args: 'regwall'
                                        })
                                        logger('info', 'Sophi.init - Sophi regwall event dispatched')

                                        // Show regwall
                                        logger('info', 'Sophi.init - Show regwall')
                                        this.showRegwall()
                                    }

                                    // Send modal view event
                                    addEventListener(IdentityEvent.MP_READY, () => {
                                        document.dispatchEvent(
                                            new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                                                detail: {
                                                    context: decision.context,
                                                    control_fields: {
                                                        mparticle: {
                                                            keys: {
                                                                context: 'context',
                                                                inputs: 'inputs',
                                                                meter_name: 'meter_name',
                                                                modal_name: 'modal_name',
                                                                trace: 'trace',
                                                                variant_name: 'variant_name'
                                                            },
                                                            mp_event_type: 'UserPreference'
                                                        }
                                                    },
                                                    event_name: AnalyticsService.customEventNames.modalView,
                                                    inputs: decision.inputs,
                                                    meter_name: 'sophi_meter',
                                                    modal_name: (wallType === WallType.REGWALL)?
                                                        'regwall_limit_reached' : 'paywall_limit_reached',
                                                    trace: decision.trace,
                                                    variant_name: variantName
                                                }
                                            })
                                        )
                                    })
                                }
                            } else {
                                logger('info', 'Sophi.init - Sophi initialized without using decision')
                                // Send meter execute event without sophi decision
                                addEventListener(IdentityEvent.MP_READY, () => {
                                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                                        detail: {
                                            control_fields: {
                                                mparticle: {
                                                    keys: {
                                                        meter_name: 'meter_name',
                                                        variant_name: 'variant_name'
                                                    },
                                                    mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                                    mp_event_type: 'UserPreference'
                                                }
                                            },
                                            event_name: AnalyticsService.customEventNames.meterExecute,
                                            meter_name: 'sophi_meter',
                                            variant_name: variantName
                                        }
                                    }))
                                })
                            }
                        } catch (e) {
                            logger('error', `Sophi.init - Error when getting sophi decision: ${JSON.stringify(e)}`)
                        }
                    }
                }
            }
        }
    }

    /**
     * Hide the article and show regwall
     */
    public showRegwall() {
        const moreOfferLink = document.querySelectorAll('.identity-screen__more-offer')
        const articleBlock = document.querySelector('#article-block')

        // Set data attribute for automation testing
        if (articleBlock) {
            articleBlock.setAttribute('data-aqa', 'anonymous-block')
        }

        // Remove more offer link.
        moreOfferLink.forEach((el) => el.remove())

        this.removeWidgetsAndAds()

        DOMUtils.addClass(
            document.querySelector('.article-content__widget-group--sidebar-slot'),
            'hidden'
        )

        DOMUtils.addClass(
            document.querySelector('.article-content__share-group'),
            'hidden'
        )

        DOMUtils.addClass(
            document.querySelector('.list-widget-trending.list'),
            'hidden'
        )

        DOMUtils.removeClass(
            document.querySelector('.article-content__sign-in-group'),
            'hidden'
        )
    }

    /**
     * Hide the article and show paywall
     */
    public showPaywall() {
        const screenForm = document.querySelector(
            '.identity-screen-form'
        )
        const articleBlock = document.querySelector('#article-block')

        // Set data attribute for automation testing
        if (articleBlock) {
            articleBlock.setAttribute('data-aqa', 'register-block')
        }

        // Remove Screen Form
        if (screenForm) {
            screenForm.remove()
        }

        this.removeWidgetsAndAds()

        DOMUtils.addClass(
            document.querySelector('.article-content__widget-group--sidebar-slot'),
            'hidden'
        )

        DOMUtils.addClass(
            document.querySelector('.article-content__share-group'),
            'hidden'
        )

        DOMUtils.addClass(
            document.querySelector('.intro-body__anonymous'),
            'hidden'
        )

        DOMUtils.addClass(
            document.querySelector('.intro-body__premium'),
            'hidden'
        )

        DOMUtils.addClass(
            document.querySelector('.list-widget-trending.list'),
            'hidden'
        )

        DOMUtils.addClass(
            document.querySelector
                ('.identity-screen'),
            'identity-screen--piano-offer'
        )

        DOMUtils.removeClass(
            document.querySelector('.article-content__sign-in-group'),
            'hidden'
        )

        DOMUtils.removeClass(
            document.querySelector('.intro-body__register'),
            'hidden'
        )

        DOMUtils.removeClass(
            document.querySelector('.identity-screen-offer'),
            'hidden'
        )

        DOMUtils.removeClass(
            document.querySelector('.identity-screen__more-offer'),
            'hidden'
        )
    }

    /**
     * Remove widgets, gallery, all ads except 2nd and 3rd position
     */
    private removeWidgetsAndAds() {
        const widgetsInContent = document.querySelectorAll(
            '.js-widget-content'
        )
        const galleryBlock = document.querySelector(
            '.gallery-block'
        )
        const articleContentSections = document.querySelectorAll('.article-content__content-group')
        const articleAdSections = document.querySelectorAll('.article-content__ad-group:not(.ad_counter_1):not(.ad_counter_2):not(.ad_counter_3)')

        // Remove All widgets in content
        widgetsInContent.forEach((el) => el.remove())

        // Remove Gallery elements and keep gallery block container to make space before excerpt
        if (galleryBlock) {
            galleryBlock.replaceChildren()
        }

        // Remove all content group sections include widgets
        articleContentSections.forEach((el) => el.remove())

        // Remove all Ads except 2nd & 3rd position of ads
        articleAdSections.forEach((el) => el.remove())
    }

    /**
     * Returns user login status
     *
     * @returns LoginStatus enum (anonymous, subscribed or registered)
     */
    private getLoginStatus() {
        const profile = LocalStore.get<IProfile>(Store.ID)

        if (!profile.email) {
            return LoginStatus.ANONYMOUS
        }

        if (profile.adLight) {
            return LoginStatus.SUBSCRIBED
        }

        return LoginStatus.REGISTERED
    }
}
